import type { Value } from '@skop-app/lib-domain/types'
import type { AdaptedValueField } from '@/services/property/property.type'

export const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isValue = (value: unknown): value is Value => {
  return typeof value === 'string' || typeof value === 'number'
}

export const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? toStringValue(value[0]) : ''
  }

  if (isRecord(value) && 'value' in value) {
    return toStringValue(value.value)
  }

  return ''
}

export const extractScalarValue = (value: unknown): Value | undefined => {
  if (isValue(value)) return value
  if (!isRecord(value)) return undefined
  return isValue(value.value) ? value.value : undefined
}

export const extractIdOrValue = (value: unknown): Value | undefined => {
  if (isValue(value)) return value
  if (!isRecord(value)) return undefined
  if (isValue(value.id)) return value.id
  if (isValue(value.value)) return value.value
  return undefined
}

export const extractLabel = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (!isRecord(value)) return ''
  return typeof value.label === 'string' ? value.label : ''
}

export const hasValueField = (
  value: unknown,
): value is {
  value: unknown
  type?: string
} => isRecord(value) && 'value' in value

export const hasLabelField = (
  value: unknown,
): value is {
  label: unknown
} => isRecord(value) && 'label' in value

export const getStringLabel = (value: unknown): string | undefined => {
  if (!hasLabelField(value)) return undefined
  return typeof value.label === 'string' ? value.label : undefined
}

export const isAdaptedField = (value: unknown): value is AdaptedValueField => {
  return (
    isRecord(value) &&
    ('label' in value || 'value' in value) &&
    (!hasLabelField(value) || typeof value.label === 'string') &&
    (!hasValueField(value) || typeof value.value === 'string')
  )
}

export const getAdaptedField = (
  product: Record<string, unknown>,
  key: string,
): AdaptedValueField | undefined => {
  const field = product[key]
  return isAdaptedField(field) ? field : undefined
}
